import { Edges, Html } from "@react-three/drei";
import { useEffect, useRef, useState } from "react";
import { useRoomStore, useUserInterfaceStore } from "../../store";
import Decorations from "./Decorations";
import DecoratedIframe from "./DecoratedIframe";
import DecoratedPlane from "./DecoratedPlane";
import DecoratedVideo from "./DecoratedVideo";
import { snapToGrid } from "../../constants/Helpers";
import RoomGeometry from "./RoomGeometry";
import UploadImageModal from "./UploadImageModal";

function RoomEditing() {
  const addDecoration = useRoomStore((s) => s.addDecoration);
  const snappingForEditor = useUserInterfaceStore((s) => s.snappingForEditor);

  const editingRoom = useUserInterfaceStore((s) => s.editingRoom);
  const roomEditingTab = useUserInterfaceStore((s) => s.roomEditingTab);
  const decorationType = useUserInterfaceStore((s) => s.decorationType);
  const decorationOption = useUserInterfaceStore((s) => s.decorationOption);
  const decorationLink = useUserInterfaceStore((s) => s.decorationLink);
  const decorationHoverText = useUserInterfaceStore(
    (s) => s.decorationHoverText
  );
  const setSelectedDecoration = useUserInterfaceStore(
    (s) => s.setSelectedDecoration
  );
  const selectedDecorationIndex = useUserInterfaceStore(
    (s) => s.selectedDecorationIndex
  );
  const [showUploadImageModal, setShowUploadImageModal] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [currentPoint, setCurrentPoint] = useState(null);
  const [drawingNormal, setDrawingNormal] = useState(null);
  const [tempDecoration, setTempDecoration] = useState(null);

  const boxRef = useRef();
  const handlePointerDown = (e) => {
    if (!editingRoom) return;

    // Deselect any selected decoration when clicking on the wall
    // (Decoration clicks have stopPropagation, so this only fires for wall clicks)
    if (selectedDecorationIndex >= 0) {
      setSelectedDecoration(null, -1);
    }

    // Only allow decoration creation when in Decorations tab (tab 1)
    // Don't create decorations when in Wall Type tab (tab 0)
    if (roomEditingTab !== 1) return;

    // Check if image decoration is selected but no valid image URL is provided
    if (
      decorationType === "image" &&
      (!decorationOption || !decorationOption?.url || decorationOption?.url?.trim() === "")
    ) {
      setShowUploadImageModal(true);
      return;
    }
    const point = e.point;
    const faceNormal = e.normal;
    if (!faceNormal.x && !faceNormal.z) return;
    setStartPoint([point.x, point.y, point.z]);
    setCurrentPoint([point.x, point.y, point.z]);
    setDrawingNormal(faceNormal);
    setIsDrawing(true);
  };

  const handlePointerMove = (e) => {
    if (isDrawing && editingRoom) {
      const point = e.point;
      const faceNormal = e.normal;

      if (!faceNormal.x && !faceNormal.z) return;

      if (snappingForEditor) {
        const snappedPoint = [
          snapToGrid(point.x, 2.5),
          snapToGrid(point.y, 2.5),
          snapToGrid(point.z, 2.5),
        ];
        setCurrentPoint(snappedPoint);
      } else {
        setCurrentPoint([e.point.x, e.point.y, e.point.z]);
      }
    }
  };

  const handlePointerUp = () => {
    if (isDrawing && editingRoom && startPoint && currentPoint) {
      const minX = Math.min(startPoint[0], currentPoint[0]);
      const minY = Math.min(startPoint[1], currentPoint[1]);
      const minZ = Math.min(startPoint[2], currentPoint[2]);

      const sizeX = Math.abs(startPoint[0] - currentPoint[0]);
      const sizeY = Math.abs(startPoint[1] - currentPoint[1]);
      const sizeZ = Math.abs(startPoint[2] - currentPoint[2]);
      // console.log(sizeX, sizeY, sizeZ, startPoint, currentPoint);

      const size = 10;
      if (sizeX > size || sizeY > size || sizeZ > size) {
        // console.log("pointerup");

        let width = 0,
          height = 0,
          position = [0, 0, 0];
        let rotation = [0, 0, 0];

        // Small offset to push decoration away from wall surface (prevents z-fighting)
        const wallOffset = 0.5;

        if (drawingNormal.x === 1) {
          // Left wall (at -X, inner face pointing +X toward room center)
          width = sizeZ;
          height = sizeY;
          position = [startPoint[0] + wallOffset, minY + sizeY / 2, minZ + sizeZ / 2];
          rotation = [0, Math.PI / 2, 0];
        } else if (drawingNormal.x === -1) {
          // Right wall (at +X, inner face pointing -X toward room center)
          width = sizeZ;
          height = sizeY;
          position = [startPoint[0] - wallOffset, minY + sizeY / 2, minZ + sizeZ / 2];
          rotation = [0, -Math.PI / 2, 0];
        } else if (Math.abs(drawingNormal.y) === 1) {
          // Floor/ceiling
          width = sizeX;
          height = sizeZ;
          position = [minX + sizeX / 2, startPoint[1], minZ + sizeZ / 2];
          rotation = [Math.PI / 2, 0, 0];
        } else if (drawingNormal.z === 1) {
          // Front wall (at -Z, inner face pointing +Z toward room center)
          width = sizeX;
          height = sizeY;
          position = [minX + sizeX / 2, minY + sizeY / 2, startPoint[2] + wallOffset];
          rotation = [0, 0, 0];
        } else if (drawingNormal.z === -1) {
          // Back wall (at +Z, inner face pointing -Z toward room center)
          width = sizeX;
          height = sizeY;
          position = [minX + sizeX / 2, minY + sizeY / 2, startPoint[2] - wallOffset];
          rotation = [0, Math.PI, 0];
        }
        let finalWidth = width;
        let finalHeight = height;

        if (decorationType === "image") {
          const { width: imgW, height: imgH } = decorationOption;

          if (imgW && imgH) {
            const imageAspect = imgW / imgH;
            const planeAspect = width / height;

            if (imageAspect > planeAspect) {
              finalWidth = width;
              finalHeight = width / imageAspect;
            } else {
              finalHeight = height;
              finalWidth = height * imageAspect;
            }
          }
          // After you compute finalWidth and finalHeight
          const offsetX = (finalWidth - width) / 2;
          const offsetY = (height - finalHeight) / 2;

          position = [
            position[0] + offsetX,
            position[1] + offsetY,
            position[2],
          ];
        }
        setTempDecoration({
          position,
          width: finalWidth,
          height: finalHeight,
          rotation,
          type: decorationType,
          option: decorationOption,
          link: decorationLink,
          hoverText: decorationHoverText,
        });
        setSelectedDecoration(null);
      } else {
        // If the click/tap was too small to create a decoration,
        // deselect the currently selected decoration if there is one
        if (selectedDecorationIndex >= 0) {
          setSelectedDecoration(null, -1);
        }
      }
    }

    setIsDrawing(false);
    setStartPoint(null);
    setCurrentPoint(null);
    setDrawingNormal(null);
  };
  useEffect(() => {
    const handleKey = (e) => {
      if (!tempDecoration) return;
      if (e.key === "Enter") {
        addDecoration({ ...tempDecoration });
        setTempDecoration(null);
      }
      if (e.key === "Escape") {
        setTempDecoration(null);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [addDecoration, tempDecoration]);

  useEffect(() => {
    setTempDecoration((prev) => {
      if (prev) {
        setSelectedDecoration(null);
      }
      return prev
        ? {
            ...prev,
            type: decorationType,
            option: decorationOption,
            link: decorationLink,
            hoverText: decorationHoverText,
          }
        : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    decorationOption,
    decorationType,
    decorationHoverText,
    decorationLink,
    // Removed setSelectedDecoration from deps - it's a stable setter function
  ]);
  return (
    <>
      <group>
      {/* Base Room */}
      <RoomGeometry
        // position={[0, height / 2 - 0.1, 0]}
        // args={[scaleX, height, scaleZ]}
        boxRef={boxRef}
        customProps={{
          onPointerDown: handlePointerDown,
          onPointerMove: handlePointerMove,
          onPointerUp: handlePointerUp,
        }}
      />

      {isDrawing && startPoint && currentPoint && drawingNormal && (
        <>
          {(() => {
            let minX = Math.min(startPoint[0], currentPoint[0]);
            let minY = Math.min(startPoint[1], currentPoint[1]);
            let minZ = Math.min(startPoint[2], currentPoint[2]);

            let sizeX = Math.abs(startPoint[0] - currentPoint[0]);
            let sizeY = Math.abs(startPoint[1] - currentPoint[1]);
            let sizeZ = Math.abs(startPoint[2] - currentPoint[2]);

            let width = 0,
              height = 0,
              position = [0, 0, 0];
            let rotation = [0, 0, 0];

            // Small offset to push decoration away from wall surface
            const wallOffset = 0.5;

            if (drawingNormal.x === 1) {
              // Left wall
              width = sizeZ;
              height = sizeY;
              position = [startPoint[0] + wallOffset, minY + sizeY / 2, minZ + sizeZ / 2];
              rotation = [0, Math.PI / 2, 0];
            } else if (drawingNormal.x === -1) {
              // Right wall
              width = sizeZ;
              height = sizeY;
              position = [startPoint[0] - wallOffset, minY + sizeY / 2, minZ + sizeZ / 2];
              rotation = [0, -Math.PI / 2, 0];
            } else if (Math.abs(drawingNormal.y) === 1) {
              // Floor/ceiling
              width = sizeX;
              height = sizeZ;
              position = [minX + sizeX / 2, startPoint[1], minZ + sizeZ / 2];
              rotation = [Math.PI / 2, 0, 0];
            } else if (drawingNormal.z === 1) {
              // Front wall
              width = sizeX;
              height = sizeY;
              position = [minX + sizeX / 2, minY + sizeY / 2, startPoint[2] + wallOffset];
              rotation = [0, 0, 0];
            } else if (drawingNormal.z === -1) {
              // Back wall
              width = sizeX;
              height = sizeY;
              position = [minX + sizeX / 2, minY + sizeY / 2, startPoint[2] - wallOffset];
              rotation = [0, Math.PI, 0];
            }

            // Create a preview decoration object with current dimensions
            const previewDecor = {
              position,
              width: width || 0.1,
              height: height || 0.1,
              rotation,
              type: decorationType,
              option: decorationOption,
              link: decorationLink,
              hoverText: decorationHoverText,
            };

            return (
              <group position={position} rotation={rotation}>
                {decorationType === "spotify" || decorationType === "youtube" ? (
                  <DecoratedIframe decor={previewDecor} />
                ) : decorationType === "image" ? (
                  <DecoratedPlane decor={previewDecor} />
                ) : decorationType === "video" ? (
                  <DecoratedVideo decor={previewDecor} video />
                ) : (
                  <mesh>
                    <planeGeometry args={[width || 0.1, height || 0.1]} />
                    <meshStandardMaterial color={decorationOption || "lightblue"} side={2} transparent opacity={0.7} />
                  </mesh>
                )}
                <Edges scale={1.01} linewidth={5} color="blue" />
              </group>
            );
          })()}
          <mesh position={startPoint}>
            <sphereGeometry args={[1, 16, 16]} />
            <meshStandardMaterial color="green" />
          </mesh>

          <mesh position={currentPoint}>
            <sphereGeometry args={[1, 16, 16]} />
            <meshStandardMaterial color="red" />
          </mesh>
        </>
      )}
      <Decorations isEditing={true} roomMesh={boxRef.current} />
      {tempDecoration && (
        <group
          position={[
            tempDecoration.position[0] * 0.99,
            tempDecoration.position[1] * 0.99,
            tempDecoration.position[2] * 0.99,
          ]}
          rotation={tempDecoration.rotation}
          scale={1}
        >
          {tempDecoration.type === "spotify" ||
          tempDecoration.type === "youtube" ? (
            <DecoratedIframe decor={tempDecoration} />
          ) : tempDecoration.type === "image" ? (
            <DecoratedPlane decor={tempDecoration} />
          ) : tempDecoration.type === "video" ? (
            <DecoratedVideo decor={tempDecoration} video />
          ) : (
            <mesh>
              <planeGeometry
                args={[tempDecoration.width, tempDecoration.height]}
              />
              <meshStandardMaterial color={tempDecoration.option} side={2} />
              <Edges scale={1.02} linewidth={1} color="red" />
            </mesh>
          )}
        </group>
      )}
      {tempDecoration && (
        <Html
          position={[
            tempDecoration.position[0],
            tempDecoration.position[1] - tempDecoration.height / 2 - 2.5,
            tempDecoration.position[2],
          ]}
          rotation={tempDecoration.rotation}
          center
        >
          <div
            style={{
              background: "black",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              padding: "5px",
              minWidth: "40px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "row", gap: "5px" }}>
              <button
                style={{
                  backgroundColor: "#3182ce", // Chakra blue.500
                  color: "white",
                  padding: "6px 6px",
                  borderRadius: "4px",
                  border: "none",
                  cursor: "pointer",
                }}
                onClick={() => {
                  addDecoration({ ...tempDecoration });
                  setTempDecoration(null);
                }}
              >
                Accept
              </button>
              <button
                style={{
                  backgroundColor: "white",
                  color: "#e53e3e", // Chakra red.500
                  padding: "6px 6px",
                  borderRadius: "4px",
                  border: "1px solid #e53e3e",
                  cursor: "pointer",
                }}
                onClick={() => setTempDecoration(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </Html>
      )}
      </group>
      {showUploadImageModal && (
        <UploadImageModal onClose={() => setShowUploadImageModal(false)} />
      )}
    </>
  );
}

export default RoomEditing;
